import React, { useState } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { 
  MessageSquare, 
  LayoutDashboard, 
  FileText, 
  Inbox, 
  Users, 
  Settings, 
  LogOut, 
  Building2,
  Activity,
  ChevronDown,
  Check,
  Loader2
} from 'lucide-react';
import { Button } from '../ui/button';
import { ScrollArea } from '../ui/scroll-area';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator } from '../ui/dropdown-menu';
import { toast } from 'sonner';

const Sidebar = () => {
  const { user, client, availableClients, logout, switchAccount, isSuperAdmin, isTeamMember } = useAuth();
  const navigate = useNavigate();
  const [switching, setSwitching] = useState(false);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const handleSwitchAccount = async (clientId) => {
    if (clientId === client?.id) return;
    setSwitching(true);
    try {
      await switchAccount(clientId);
      navigate('/dashboard');
      toast.success('Switched account successfully');
    } catch (error) {
      toast.error('Failed to switch account');
    } finally {
      setSwitching(false);
    }
  };

  const superAdminLinks = [
    { to: '/admin/clients', icon: Building2, label: 'Clients' },
    { to: '/admin/activity', icon: Activity, label: 'Activity Logs' },
  ];

  const clientLinks = [
    { to: '/dashboard', icon: LayoutDashboard, label: 'Dashboard' },
    { to: '/templates', icon: FileText, label: 'Templates' },
    { to: '/inbox', icon: Inbox, label: 'Inbox' },
    ...(!isTeamMember ? [{ to: '/team', icon: Users, label: 'Team' }] : []),
    { to: '/settings', icon: Settings, label: 'Settings' },
  ];

  const navLinks = isSuperAdmin ? superAdminLinks : clientLinks;

  return (
    <div className="w-60 h-screen bg-white border-r border-[#D9E2E5] flex flex-col fixed left-0 top-0">
      {/* Logo + Account Switcher */}
      <div className="p-5 border-b border-[#D9E2E5]">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-[#131517] rounded-lg flex items-center justify-center flex-shrink-0">
            <MessageSquare className="w-5 h-5 text-white" />
          </div>
          <div className="flex-1 min-w-0">
            <h1 className="text-[16px] font-semibold text-[#131517]">WhatsApp CRM</h1>
            {!isSuperAdmin && availableClients.length > 1 ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button
                    className="flex items-center gap-1 text-[11px] text-[#8A8A8B] hover:text-[#595C5C] transition-colors mt-0.5 max-w-full"
                    disabled={switching}
                    data-testid="account-switcher-trigger"
                  >
                    {switching ? (
                      <Loader2 className="w-3 h-3 animate-spin" />
                    ) : null}
                    <span className="truncate">{client?.name || 'Select Account'}</span>
                    <ChevronDown className="w-3 h-3 flex-shrink-0" />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent side="bottom" align="start" className="w-52">
                  <div className="px-2 py-1.5">
                    <p className="text-[10px] font-semibold text-[#8A8A8B] uppercase tracking-wide">Switch Account</p>
                  </div>
                  <DropdownMenuSeparator />
                  {availableClients.map((ac) => (
                    <DropdownMenuItem
                      key={ac.id}
                      onClick={() => handleSwitchAccount(ac.id)}
                      className="flex items-center justify-between cursor-pointer"
                      data-testid={`switch-account-${ac.id}`}
                    >
                      <div className="flex items-center gap-2 min-w-0">
                        <div className="w-6 h-6 bg-[#EFEFEF] rounded flex items-center justify-center flex-shrink-0">
                          <Building2 className="w-3 h-3 text-[#595C5C]" />
                        </div>
                        <span className="text-[13px] truncate">{ac.name}</span>
                      </div>
                      {ac.id === client?.id && (
                        <Check className="w-3.5 h-3.5 text-[#00A884] flex-shrink-0 ml-2" />
                      )}
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <p className="text-[11px] text-[#8A8A8B]">
                {isSuperAdmin ? 'Super Admin' : client?.name || 'Dashboard'}
              </p>
            )}
          </div>
        </div>
      </div>

      {/* Navigation */}
      <ScrollArea className="flex-1 py-4">
        <nav className="px-3 space-y-1">
          {navLinks.map((link) => (
            <NavLink
              key={link.to}
              to={link.to}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 rounded-lg text-[14px] font-medium transition-colors ${
                  isActive
                    ? 'bg-[#131517] text-white'
                    : 'text-[#595C5C] hover:bg-[#EFEFEF] hover:text-[#131517]'
                }`
              }
              data-testid={`nav-${link.label.toLowerCase().replace(' ', '-')}`}
            >
              <link.icon className="w-[18px] h-[18px]" />
              {link.label}
            </NavLink>
          ))}
        </nav>
      </ScrollArea>

      {/* User Section */}
      <div className="p-4 border-t border-[#D9E2E5]">
        <div className="flex items-center gap-3 mb-3">
          <div className="w-9 h-9 bg-[#EFEFEF] rounded-full flex items-center justify-center">
            <span className="text-[14px] font-semibold text-[#595C5C]">
              {user?.name?.charAt(0)?.toUpperCase() || 'U'}
            </span>
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-[13px] font-medium text-[#131517] truncate">{user?.name}</p>
            <p className="text-[11px] text-[#8A8A8B] truncate">{user?.email}</p>
          </div>
        </div>
        <Button
          variant="ghost"
          className="w-full justify-start text-[#595C5C] hover:text-[#EB493D] hover:bg-[#EB493D]/10 h-9"
          onClick={handleLogout}
          data-testid="logout-btn"
        >
          <LogOut className="w-4 h-4 mr-2" />
          Logout
        </Button>
      </div>
    </div>
  );
};

export default Sidebar;
